import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { User, Star, Lock, CalendarOff, CalendarClock, Mail, Loader2 } from 'lucide-react';
import { getMediationAreaLabel } from '@/lib/mediationAreaMapping';
import { trackUiEvent } from '@/lib/uiEventTracking';
import { useMediatorAvailabilityEstimate } from '@/hooks/useMediatorAvailabilityEstimate';

export interface MediatorResultCardData {
  id?: string | null;
  is_verified?: boolean | null;
  rating?: number | null;
  mediation_areas?: string[] | null;
  info_meeting_price_cents?: number | null;
  info_meeting_duration_min?: number | null;
  mediation_meeting_total_price_cents?: number | null;
  mediation_meeting_duration_min?: number | null;
  profiles?: {
    full_name?: string | null;
    city?: string | null;
  } | null;
}

interface MediatorResultCardProps {
  mediator: MediatorResultCardData;
  /** When false, renders a lock icon and a "sign-in" CTA instead of "Voir le profil". */
  isAuthenticated?: boolean;
  /** Override the primary action (e.g., booking flow). */
  onAction?: () => void;
  actionLabel?: string;
  /** Hide the action button (e.g., when the card is the headline of a public profile page). */
  hideAction?: boolean;
  /** When false, shows a "sans créneaux" badge to indicate the mediator has no future availability. */
  hasFutureSlots?: boolean;
  /** When true, renders a skeleton placeholder instead of the badge while availability is loading. */
  isLoadingSlots?: boolean;
  /** Identifier for analytics so we can compare placements (e.g. 'search_widget', 'seeker_page'). */
  trackingSource?: string;
}

export const MediatorResultCard = ({
  mediator,
  isAuthenticated = true,
  onAction,
  actionLabel,
  hideAction = false,
  hasFutureSlots = true,
  isLoadingSlots = false,
  trackingSource,
}: MediatorResultCardProps) => {
  const navigate = useNavigate();
  // Tracks whether the visitor opened the "Sans créneaux" popover on this
  // card. Lets us measure the conversion: popover open → profile click.
  const noSlotsPopoverOpenedRef = useRef(false);
  // Drives lazy-fetching of the historical availability estimate: we only
  // hit the DB once the popover actually opens.
  const [estimateEnabled, setEstimateEnabled] = useState(false);

  const { data: estimate, isLoading: estimateLoading } =
    useMediatorAvailabilityEstimate(mediator.id, { enabled: estimateEnabled });

  const handleClick = () => {
    void trackUiEvent('mediator_profile_click', {
      mediatorId: mediator.id ?? null,
      source: trackingSource,
      context: {
        hasFutureSlots,
        afterNoSlotsPopover: noSlotsPopoverOpenedRef.current,
      },
    });

    if (!hasFutureSlots && noSlotsPopoverOpenedRef.current) {
      void trackUiEvent('no_slots_profile_click', {
        mediatorId: mediator.id ?? null,
        source: trackingSource,
      });
    }

    if (onAction) {
      onAction();
      return;
    }
    if (!isAuthenticated) {
      navigate('/connexion?mode=signup');
      return;
    }
    if (mediator.id) {
      navigate(`/mediateur/${mediator.id}`);
    }
  };

  const handleNoSlotsPopoverOpenChange = (open: boolean) => {
    if (open) {
      // Trigger the historical estimate query (lazy).
      setEstimateEnabled(true);
      if (!noSlotsPopoverOpenedRef.current) {
        noSlotsPopoverOpenedRef.current = true;
        void trackUiEvent('no_slots_badge_open', {
          mediatorId: mediator.id ?? null,
          source: trackingSource,
        });
      }
    }
  };

  const ctaLabel =
    actionLabel ?? (isAuthenticated ? 'Voir le profil' : 'Se connecter pour voir');

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              <User className="h-4 w-4" />
              {mediator.profiles?.full_name}
              {!isAuthenticated && (
                <Lock className="h-3 w-3 text-muted-foreground" />
              )}
            </h4>
            <p className="text-xs text-muted-foreground">{mediator.profiles?.city}</p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {mediator.is_verified && (
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">✓</span>
            )}
            {isLoadingSlots ? (
              <Skeleton
                aria-label="Chargement de la disponibilité"
                className="h-5 w-24 rounded-full"
              />
            ) : !hasFutureSlots ? (
              <Popover
                modal={false}
                onOpenChange={handleNoSlotsPopoverOpenChange}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    // Stop both pointer + click bubbling so an enclosing
                    // clickable card never steals the tap on mobile.
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Ce médiateur n'a aucun créneau publié — voir les détails"
                    className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <Badge
                      variant="outline"
                      className="text-[10px] font-normal gap-1 border-muted-foreground/30 text-muted-foreground cursor-help hover:bg-muted/50 transition-colors"
                    >
                      <CalendarOff className="h-3 w-3" />
                      Sans créneaux
                    </Badge>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="end"
                  // Keep at least 12px from the viewport edges so the bubble
                  // never gets clipped on narrow phones (≤375 px).
                  collisionPadding={12}
                  sideOffset={6}
                  // Cap width on very small screens.
                  className="w-[min(18rem,calc(100vw-2rem))] text-xs space-y-2"
                  onClick={(e) => e.stopPropagation()}
                  // Radix handles outside-click and Escape natively; we just
                  // stop those events from bubbling up into the surrounding
                  // card so they don't trigger a parent navigation.
                  onPointerDownOutside={(e) => {
                    e.detail.originalEvent.stopPropagation();
                  }}
                  onEscapeKeyDown={(e) => e.stopPropagation()}
                  // Radix returns focus to the trigger by default when closed,
                  // which is the correct behaviour for keyboard navigation —
                  // we deliberately don't override onCloseAutoFocus.
                >
                  <div className="flex items-start gap-2">
                    <CalendarOff className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">Aucun créneau publié</p>
                      <p className="text-muted-foreground leading-relaxed">
                        Ce médiateur n'a publié aucune disponibilité dans son agenda public.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 pt-2 border-t">
                    <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="space-y-1 leading-relaxed">
                      <p className="text-muted-foreground">
                        <span className="font-medium text-foreground">Prochaine disponibilité estimée :</span>{' '}
                        {estimateLoading || !estimate ? (
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            calcul en cours…
                          </span>
                        ) : (
                          estimate.label
                        )}
                      </p>
                      {estimate && !estimateLoading && (
                        <p className="text-[11px] text-muted-foreground">
                          {estimate.isFallback
                            ? 'Estimation par défaut : ce médiateur n\'a publié aucun créneau ces 4 dernières semaines.'
                            : `Basée sur ${estimate.publishedDays} jour${estimate.publishedDays > 1 ? 's' : ''} publié${estimate.publishedDays > 1 ? 's' : ''} sur les 4 dernières semaines.`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 pt-2 border-t">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed">
                      Vous pouvez consulter son profil et le contacter pour demander une date.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full mt-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Authenticated visitors land on the mediator's public
                      // profile (where pricing + bio give them context to
                      // request a date). Anonymous visitors are sent to the
                      // generic contact form so they can ask without an account.
                      if (mediator.id && isAuthenticated) {
                        navigate(`/mediateur/${mediator.id}`);
                      } else if (mediator.id) {
                        navigate(`/connexion?mode=signup&redirect=/mediateur/${mediator.id}`);
                      } else {
                        navigate('/contact');
                      }
                    }}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Contacter pour demander une date
                  </Button>
                </PopoverContent>
              </Popover>
            ) : null}
          </div>
        </div>

        <div className="space-y-1 mb-3">
          {mediator.mediation_areas && mediator.mediation_areas.length > 0 && (
            <p className="text-xs">
              <strong>Domaines:</strong>{' '}
              {mediator.mediation_areas.slice(0, 2).map(getMediationAreaLabel).join(', ')}
              {mediator.mediation_areas.length > 2 && '...'}
            </p>
          )}
          {mediator.info_meeting_price_cents != null && (
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="outline" className="text-[10px] font-normal">
                1er RDV : {(mediator.info_meeting_price_cents / 100).toFixed(0)} € ·{' '}
                {mediator.info_meeting_duration_min ?? 30} min
              </Badge>
              <Badge variant="outline" className="text-[10px] font-normal">
                Médiation :{' '}
                {((mediator.mediation_meeting_total_price_cents ?? 20000) / 200).toFixed(0)}{' '}
                €/partie · {mediator.mediation_meeting_duration_min ?? 60} min
              </Badge>
            </div>
          )}
          {mediator.rating != null && mediator.rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              <span className="text-xs font-semibold">{mediator.rating.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">/ 5</span>
            </div>
          )}
        </div>

        {!hideAction && (
          <Button size="sm" onClick={handleClick} className="w-full">
            {ctaLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
